import { useCallback, useMemo } from "react";
import type { EventListGridItem } from "src/http/types";
import { eventHasNoTicketsAvailable } from "src/website-apps/shared/hooks/useIsTicketsNotAvailable";

/**
 * Custom hook to determine the ticket availability for the given events.
 * Returns the availability for each event, whether tickets are available for all events,
 * and whether tickets are available for any event.
 * @param events - Array of events. If a single event is provided, it is converted to an array.
 * @returns Object containing eventAvailability, areTicketsAvailableForAllEvents, and areTicketsAvailableForAnyEvent.
 */
export const useTicketAvailability = (events: EventListGridItem[]) => {
  const allEvents = useMemo(() => (Array.isArray(events) ? events : [events]), [events]);

  const areTicketsUnavailable = useCallback((event: EventListGridItem) => eventHasNoTicketsAvailable(event), []);

  const eventAvailability = useMemo(
    () => allEvents.map((event) => !areTicketsUnavailable(event)),
    [allEvents, areTicketsUnavailable],
  );

  const areTicketsAvailableForAllEvents = useMemo(
    () => Object.values(eventAvailability).every(Boolean),
    [eventAvailability],
  );

  const areTicketsAvailableForAnyEvent = useMemo(
    () => Object.values(eventAvailability).some(Boolean),
    [eventAvailability],
  );

  return {
    eventAvailability,
    areTicketsAvailableForAllEvents,
    areTicketsAvailableForAnyEvent,
  };
};
